import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'
import { FileStorageService } from '../../../../../services/FileStorageService'
import { DxfParserService } from '../../../../../services/DxfParserService'
import { useFloorStore } from '../../../../../map-editor-shared/store/floorStore'
import { FileInfo } from '../../../../../models/FileStorage'
import { DwgCleanCancelled } from './useDwgCleanStep'
import type { ViewMode } from '../types'

interface UseServerDownloadDeps {
  setViewMode: (mode: ViewMode) => void
  setUploadState: (state: any) => void
  setFile: (file: File) => void
  parseFile: () => Promise<void>
  setDownloadError: (error: string | null) => void
  setDownloadMessage: (message: string) => void
  setSelectedDxfFile: (file: FileInfo | null) => void
  setAnalyzedLayers: (layers: any[]) => void
  setShowLayerModal: (show: boolean) => void
  onMappingRequired?: () => void
  resolveDwg: (file: FileInfo) => Promise<FileInfo>
  currentProjectId: number | null
  siteProjectId: number | null
  currentLot: string | null
  storeCurrentFloor: string | null
  currentFloor?: string | null
}

export function useServerDownload(deps: UseServerDownloadDeps) {
  const {
    setViewMode, setUploadState, setFile, parseFile,
    setDownloadError, setDownloadMessage,
    setSelectedDxfFile, setAnalyzedLayers, setShowLayerModal,
    onMappingRequired, resolveDwg, currentProjectId, siteProjectId, currentLot, storeCurrentFloor, currentFloor,
  } = deps

  const handleServerCSVFileSelect = async (file: FileInfo) => {
    if (!siteProjectId) {
      setDownloadError('사이트 프로젝트 ID가 없습니다.')
      return
    }

    try {
      log.info(LogCategory.CSV, 'Server CSV file selected', {
        siteProjectId,
        mapId: currentProjectId,
        filename: file.filename,
        originalName: file.original_name,
      })

      setViewMode('downloading')
      setDownloadMessage('CSV 파일 다운로드 중...')
      setUploadState({
        status: 'uploading',
        progress: 50
      })

      // Download CSV file from server (flat {projectId}/uploads/csv/ path)
      const blob = await FileStorageService.downloadFile(String(siteProjectId), 'csv', file.filename)
      log.info(LogCategory.CSV, 'CSV file downloaded from server', { size: blob.size })

      setDownloadMessage('CSV 데이터 처리 중...')
      setUploadState({
        status: 'uploading',
        progress: 80
      })

      // Create File object from blob
      const csvFile = new File([blob], file.original_name || file.filename, { type: 'text/csv' })

      setDownloadMessage('CSV 분석 중...')
      setUploadState({
        status: 'uploading',
        progress: 90
      })

      // Set file and trigger parsing
      setFile(csvFile)
      setViewMode('parsing')

      // Parse the file
      await parseFile()

      log.info(LogCategory.CSV, 'Server CSV file parsed successfully')
      setViewMode('parsed')

      // Trigger layer mapping modal after successful parsing
      if (onMappingRequired) {
        onMappingRequired()
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'CSV 파일 다운로드/분석에 실패했습니다.'
      log.error(LogCategory.CSV, 'Server CSV processing failed', { error: errorMessage })

      setDownloadError(errorMessage)
      setViewMode('error')
      setUploadState({
        status: 'error',
        message: errorMessage
      })
    }
  }

  const handleCADFileSelect = async (file: FileInfo) => {
    if (!currentLot) {
      setDownloadError('선택된 프로젝트가 없습니다.')
      return
    }

    if (!siteProjectId) {
      setDownloadError('사이트 프로젝트 ID가 없습니다.')
      return
    }

    // Auto-create B1 floor if no floor exists
    if (!storeCurrentFloor && !currentFloor) {
      if (!currentLot) {
        setDownloadError('프로젝트를 먼저 선택해주세요.')
        setViewMode('error')
        return
      }
      log.info(LogCategory.CSV, 'No floor exists, auto-creating default B1 floor', { lotId: currentLot })
      useFloorStore.getState().addFloor(currentLot)
    }

    try {
      const activeFloor = useFloorStore.getState().currentFloor || storeCurrentFloor || currentFloor
      log.info(LogCategory.CSV, 'CAD/DXF file selected', {
        siteProjectId,
        mapProjectId: currentProjectId,
        filename: file.filename,
        originalName: file.original_name,
        currentFloor: activeFloor
      })

      // DWG 면 맵에 필요 없는 레이어를 지운 DXF 로 바꾼다(남길 레이어는 사람이 확인).
      const dxfFile = await resolveDwg(file)

      // Store selected DXF file for later processing
      setSelectedDxfFile(dxfFile)

      setViewMode('downloading')
      setDownloadMessage('DXF 레이어 분석 중...')
      setUploadState({
        status: 'uploading',
        progress: 20
      })

      // Step 1: Analyze DXF layers from the flat {projectId}/uploads/cad/ path.
      log.info(LogCategory.CSV, 'Analyzing DXF layers', { dxfFilename: dxfFile.filename })
      const analysisResult = await DxfParserService.analyzeLayers(siteProjectId, dxfFile.filename)

      if (!analysisResult.success || !analysisResult.data) {
        throw new Error(analysisResult.message || 'DXF 레이어 분석 실패')
      }

      log.info(LogCategory.CSV, 'DXF layer analysis complete', {
        totalLayers: analysisResult.data.totalLayers
      })

      // Step 2: Show layer selection modal
      setAnalyzedLayers(analysisResult.data.layers)
      setViewMode('source-select') // Keep source selector visible with modal overlay
      setUploadState({ status: 'idle' }) // Reset upload state to stop progress bar
      setShowLayerModal(true)

    } catch (error) {
      if (error instanceof DwgCleanCancelled) {
        setUploadState({ status: 'idle' })
        setViewMode('server-cad')
        return
      }
      const errorMessage = error instanceof Error ? error.message : 'CAD 파일 분석에 실패했습니다.'
      log.error(LogCategory.CSV, 'CAD analysis failed', { error: errorMessage })

      setDownloadError(errorMessage)
      setViewMode('error')
      setUploadState({
        status: 'error',
        message: errorMessage
      })
    }
  }

  return { handleServerCSVFileSelect, handleCADFileSelect }
}
